import { Button, cn } from '@hemmafixa/ui';
import { Maximize2 } from 'lucide-react';
import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { useI18n } from '@/i18n/context';

interface SelectableCardProps {
  /** The item's own page; Ctrl/Cmd-click still opens it */
  to: string;
  selected: boolean;
  onSelect: () => void;
  children: ReactNode;
  className?: string;
}

/** One row of a MasterDetail list as a card: a click shows the item in the panel instead of leaving the list. */
export function SelectableCard({ to, selected, onSelect, children, className }: SelectableCardProps) {
  return (
    <Link
      to={to}
      aria-current={selected || undefined}
      onClick={(event) => {
        if (event.metaKey || event.ctrlKey || event.shiftKey) return;
        event.preventDefault();
        onSelect();
      }}
      className={cn(
        'grid gap-1.5 rounded-xl border border-transparent bg-surface p-4 shadow-sm transition-colors hover:border-primary/50',
        'focus-visible:outline-2 focus-visible:outline-ring',
        selected && 'border-primary ring-1 ring-primary',
        className,
      )}
    >
      {children}
    </Link>
  );
}

/** Link to the selected item's full page; DetailPanel shows it, or a page puts it beside its tabs. */
export function FullPageLink({ to, className }: { to: string; className?: string }) {
  const { t } = useI18n();
  return (
    <Button asChild variant="ghost" size="sm" className={cn('text-muted-foreground', className)}>
      <Link to={to}>
        <Maximize2 aria-hidden />
        {t.common.openFullPage}
      </Link>
    </Button>
  );
}

/**
 * The details side of a MasterDetail page, with a link to the item's full page. With `linkBesideTabs` the page shows
 * FullPageLink beside its tabs from `lg`, so the details start level with the list; the panel keeps it on phones.
 */
export function DetailPanel({
  fullPage,
  linkBesideTabs,
  children,
}: {
  fullPage: string;
  linkBesideTabs?: boolean;
  children: ReactNode;
}) {
  // No card of its own: the record's sections are the cards, so nothing is boxed twice
  return (
    <div className="grid gap-3">
      <FullPageLink to={fullPage} className={cn('-mb-1 justify-self-end', linkBesideTabs && 'lg:hidden')} />
      {children}
    </div>
  );
}
